import { defineRouter } from '#q-app'
import { createMemoryHistory, createRouter, createWebHashHistory, createWebHistory } from 'vue-router'
import routes from './routes'
import { useAuthStore } from 'src/entities/auth'
import { useAccessStore } from 'src/entities/access'
import { checkAccess, getRouteMeta, type RouteMeta } from 'src/shared/lib/access'

export default defineRouter(function (/* { store, ssrContext } */) {
  // app-vite 3: константы сборки — `import.meta.env.QUASAR_*` (подставляет quasar-config-file.js
  // из build.vueRouterMode / build.vueRouterBase), `process.env.*` больше не определён.
  const createHistory = import.meta.env.QUASAR_SERVER
    ? createMemoryHistory
    : (import.meta.env.QUASAR_VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory)

  const router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,
    history: createHistory(import.meta.env.QUASAR_VUE_ROUTER_BASE),
  })

  router.beforeEach(async (to, from) => {
    if (to.path === '/auth') return true

    const auth = useAuthStore()
    if (!auth.isAuthenticated) {
      return { path: '/auth' }
    }

    // Проверка meta guard (tenant, role, капабилити). Права (`q.access.me`) на первом
    // переходе ждём ограниченно: не успели — гард работает по ролям JWT (эпик ACCESS).
    const meta = getRouteMeta(to)
    const access = useAccessStore()
    if (meta && (meta.capAnyOf || meta.capAllOf || meta.menu || to.path === '/')) await access.ensureLoaded()
    // Пульс доступен — и по правам, и по галочке меню (O27).
    const pulseAllowed = () =>
      checkAccess(auth.tenantId, auth.roles, router.resolve('/pulse').meta?.guard as RouteMeta | undefined, access)
    // Посадка после входа (или открытие приложения) — Пульс, если он доступен: дашборд
    // теперь открыт и кастомным ролям с чтением транков/очередей (у оператора оно есть),
    // но стартовая страница оператора не должна смениться. Из меню дашборд открывается.
    const landing = from.matched.length === 0 || from.path === '/auth'
    if (to.path === '/' && landing && access.loaded && !access.superuser && pulseAllowed()) {
      return { path: '/pulse' }
    }
    if (meta && !checkAccess(auth.tenantId, auth.roles, meta, access)) {
      // Корень — общая посадочная страница после логина (LoginPage всегда шлёт
      // на '/'), но дашборд admin-only. Не-админа отправляем на стартовую страницу,
      // а не на «Доступ запрещён» сразу после входа: Пульс, если он доступен, иначе
      // первую доступную (кастомная роль «управляющий транками» без Пульса, ACCESS).
      if (to.path === '/') {
        if (!access.loaded || pulseAllowed()) return { path: '/pulse' }
        const firstAllowed = router
          .getRoutes()
          .find((route) => {
            if (route.path === '/' || route.path.includes(':') || !route.path.startsWith('/') || route.path === '/auth') return false
            const guard = route.meta?.guard as RouteMeta | undefined
            return guard !== undefined && checkAccess(auth.tenantId, auth.roles, guard, access)
          })
        if (firstAllowed) return { path: firstAllowed.path }
      }
      return { path: '/forbidden', query: { reason: 'forbidden' } }
    }

    return true
  })

  return router
})
